import { Injectable } from '@angular/core';
import { HttpBackend, HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { BehaviorSubject, Observable } from 'rxjs';
import { IAuthInterface, IAuthResponse, ILoginResponse, IResetPassword } from '@lib/interfaces/auth.interface';
import { environment } from '../../../../environments/environment';
import { DeepPartial } from '@lib/interfaces';
import { Store } from '@ngrx/store';
import { AppState } from '@store/app.state';
import { setPermissions } from '@store/actions/permission.actions';
import { TenantService } from '../tenant.service';

@Injectable({
    providedIn: 'root',
})
export class AuthService {
    private environment_url: string;
    private bypassHttp: HttpClient;
    private readonly loginClientId = 'pmdash';

    public isRefreshing = false;
    public readonly refreshTokenSubject: BehaviorSubject<string | null> = new BehaviorSubject<string | null>(null);

    constructor(private readonly http: HttpClient, private readonly httpHandler: HttpBackend, private readonly store: Store<AppState>, private readonly tenantService: TenantService) {
        this.environment_url = environment.api_url;
        this.bypassHttp = new HttpClient(httpHandler);
    }

    loginWithCredentials(credentials: DeepPartial<IAuthInterface>, tenantId?: string): Observable<IAuthResponse> {
        const body = { ...credentials, client_id: this.loginClientId };
        if (tenantId) {
            const headers = new HttpHeaders({
                'X-Consumer-Username': tenantId,
            });
            return this.http.post<IAuthResponse>(`${this.environment_url}nfront/v1/admins/login`, body, { headers });
        }
        return this.http.post<IAuthResponse>(`${this.environment_url}nfront/v1/admins/login`, body);
    }

    continueWithVerification(id: string, code: string, verificationType: 'mobile' | 'email'): Observable<IAuthResponse> {
        let url = `${this.environment_url}nfront/v1/admins/login/${id}:continueWithMobileVerification?code=${code}`;

        if (verificationType === 'email') {
            url = `${this.environment_url}nfront/v1/admins/login/${id}:continueWithEmailVerification?code=${code}`;
        }

        return this.http.put<IAuthResponse>(url, '');
    }

    signinWithCustomToken(loginResponse: IAuthResponse, tenantId?: string): Observable<ILoginResponse> {
        this.setLocalStorage(loginResponse);

        const consumerUsername = tenantId || '';
        const params = new HttpParams({ fromObject: { custom_token: loginResponse.custom_token?.token ?? '' } });
        const headers = new HttpHeaders({
            'Content-Type': 'application/x-www-form-urlencoded',
            'X-Consumer-Username': consumerUsername,
        });
        const url = `${this.environment_url}nfront/v3/oauth2/token:signinWithCustomToken`;
        return this.bypassHttp.post<ILoginResponse>(url, params, { headers, withCredentials: true });
    }

    refreshToken(xConsumerUsername: string): Observable<ILoginResponse> {
        const headers = new HttpHeaders({
            'X-Consumer-Username': xConsumerUsername,
        });
        const url = `${this.environment_url}nfront/v3/oauth2/token:refreshToken`;
        return this.bypassHttp.post<ILoginResponse>(url, {}, { headers, withCredentials: true });
    }

    continueWithChangePassword(adminId: string, body: { old_password: string; new_password: string }): Observable<IAuthResponse> {
        const url = `${this.environment_url}nfront/v1/admins/login/${adminId}:continueWithPasswordChange`;
        return this.http.put<IAuthResponse>(url, body);
    }

    changePassword(adminId: string, body: { old_password: string; new_password: string }): Observable<any> {
        const url = `${this.environment_url}nfront/v1/admins/${adminId}:changePassword`;
        return this.http.put<any>(url, body);
    }

    changePasswordV2(adminId: string | null, token: string, body: { old_password: string; new_password: string }): Observable<IAuthResponse> {
        const url = `${this.environment_url}nfront/v1/admins/sessions/password/forgot:changePassword?id=${adminId ?? ''}&token=${token}`;
        const headers = new HttpHeaders({
            'X-Consumer-Username': this.tenantService.getTenantKey() || '',
            apikey: this.tenantService.getTenantKey() || '',
        });
        return this.http.post<IAuthResponse>(url, { password: body.new_password }, { headers });
    }

    resetPassword(data: IResetPassword): Observable<any> {
        const url = `${this.environment_url}nfront/v1/admins/clients/password-reset`;
        return this.http.post<any>(url, data);
    }

    forgotPassword(email: string): Observable<any> {
        const url = `${this.environment_url}nfront/v1/admins/sessions/password/forgot/${email}`;
        const headers = new HttpHeaders({
            'X-Consumer-Username': this.tenantService.getTenantKey() || '',
            apikey: this.tenantService.getTenantKey() || '',
        });
        return this.http.post<any>(url, {}, { headers });
    }

    private setLocalStorage(loginResponse: IAuthResponse): void {
        localStorage.setItem('adminId', loginResponse.id);

        if (loginResponse.custom_token?.scopes?.length) {
            localStorage.setItem('permissions', JSON.stringify(loginResponse.custom_token.scopes));
            this.store.dispatch(setPermissions({ permissions: loginResponse.custom_token.scopes }));
        }

        if (loginResponse.admin) {
            localStorage.setItem('id', loginResponse.admin.id);
            localStorage.setItem('admin', `${loginResponse.admin.first_name} ${loginResponse.admin.last_name}`);
        }
    }
}
